import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SUBSCRIPTION_PLANS } from '../types';
import EventPhotoPrompt from './EventPhotoPrompt';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, staffAccounts } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showWorkDropdown, setShowWorkDropdown] = useState(false);

  const currentPlan = user ? SUBSCRIPTION_PLANS[user.subscription] : SUBSCRIPTION_PLANS.free;

  // Navigation items based on subscription tier
  const getNavItems = () => {
    const baseItems = [
      { path: '/', label: 'Dashboard', icon: '🏠' },
      { path: '/search', label: 'Search', icon: '🔍' },
      { path: '/orders', label: 'Orders', icon: '📦' },
      { path: '/subscriptions', label: 'Subscriptions', icon: '🔄' },
      { path: '/business-crm', label: 'Business Hub', icon: '🧭' },
      { path: '/inbox', label: 'Business Inbox', icon: '📬' },
      { path: '/social-hub', label: 'Social Hub', icon: '🧑‍🤝‍🧑' },
      { path: '/wallet', label: 'Wallet', icon: '💰' },
      { path: '/messages', label: 'Messages', icon: '💬' },
      { path: '/calendar', label: 'Calendar', icon: '📅' },
      { path: '/workplaces', label: 'Work', icon: '💼' },
      { path: '/jobs', label: 'Jobs', icon: '🔎' },
      { path: '/tickets', label: 'Tickets', icon: '🎫' },
      { path: '/businesses', label: 'Discover', icon: '🏪' },
    ];

    // Social tier+ features
    const socialItems = [
      { path: '/marketplace', label: 'Marketplace', icon: '🛒' },
      { path: '/groups', label: 'Groups', icon: '👥' },
      { path: '/events', label: 'Events', icon: '🎉' },
      { path: '/connections', label: 'Friends', icon: '🤝' },
      { path: '/gift-registries', label: 'Gift Registry', icon: '🎁' },
      { path: '/activity', label: 'Activity', icon: '📊' },
    ];

    // Pro tier+ features
    const proItems = [
      { path: '/finance', label: 'Finance', icon: '📊' },
      { path: '/tax', label: 'Tax', icon: '📋' },
      { path: '/portfolio', label: 'Portfolio', icon: '📈' },
      { path: '/documents', label: 'Documents', icon: '📁' },
      { path: '/vault', label: 'Vault', icon: '🔒' },
      { path: '/more', label: 'More', icon: '✨' },
    ];

    let items = [...baseItems];

    if (user && ['social', 'pro', 'premium'].includes(user.subscription)) {
      items = [...items, ...socialItems];
    }

    if (user && ['pro', 'premium'].includes(user.subscription)) {
      items = [...items, ...proItems];
    }

    return items;
  };

  const navItems = getNavItems();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'social': return '#667eea';
      case 'pro': return '#f093fb';
      case 'premium': return '#ffd700';
      default: return '#28a745';
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f8f9fa',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Top Navigation Bar */}
      <header style={{
        background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
        padding: '0 20px',
        boxShadow: '0 2px 10px rgba(255, 107, 53, 0.3)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '60px'
        }}>
          {/* Logo */}
          <div 
            onClick={() => navigate('/')}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: '28px' }}>🥭</span>
            <span style={{ 
              fontSize: '22px', 
              fontWeight: '700', 
              color: 'white',
              letterSpacing: '-0.5px'
            }}>
              Mango
            </span>
            <span style={{
              fontSize: '11px',
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              padding: '3px 8px',
              borderRadius: '12px',
              marginLeft: '5px'
            }}>
              Personal
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav style={{
            display: 'flex',
            gap: '5px',
            alignItems: 'center',
          }} className="desktop-nav">
            {navItems.slice(0, 6).map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  background: isActive(item.path) ? 'rgba(255,255,255,0.2)' : 'transparent',
                  border: 'none',
                  color: 'white',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '14px',
                  fontWeight: isActive(item.path) ? '600' : '400',
                  transition: 'background 0.2s'
                }}
              >
                <span>{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
            
            {/* More dropdown for additional items */}
            {navItems.length > 6 && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    color: 'white',
                    padding: '8px 14px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  More ▾
                </button>
                {isMobileMenuOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    padding: '10px',
                    minWidth: '200px',
                    marginTop: '10px'
                  }}>
                    {navItems.slice(6).map((item) => (
                      <button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{
                          width: '100%',
                          background: isActive(item.path) ? '#fff3e0' : 'transparent',
                          border: 'none',
                          color: '#333',
                          padding: '12px 15px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          fontSize: '14px',
                          textAlign: 'left'
                        }}
                      >
                        <span>{item.icon}</span>
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Right Side - Work as Staff dropdown, Wallet & Profile */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {/* Work as Staff - dropdown when user has multiple businesses */}
            {staffAccounts && staffAccounts.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowWorkDropdown(!showWorkDropdown)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    color: 'white',
                    padding: '8px 14px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                  }}
                >
                  <span>💼</span>
                  <span>Work at {staffAccounts.length === 1 ? staffAccounts[0].business.name : '...'}</span>
                  <span>▾</span>
                </button>
                {showWorkDropdown && (
                  <>
                    <div
                      style={{ position: 'fixed', inset: 0, zIndex: 98 }}
                      onClick={() => setShowWorkDropdown(false)}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                      padding: '8px',
                      minWidth: '220px',
                      marginTop: '8px',
                      zIndex: 99,
                    }}>
                      <div style={{ fontSize: '11px', color: '#888', padding: '4px 8px', marginBottom: '4px' }}>
                        Switch business
                      </div>
                      {staffAccounts.map((sa) => (
                        <button
                          key={sa.id}
                          onClick={() => {
                            setShowWorkDropdown(false);
                            localStorage.setItem('activeWorkplace', JSON.stringify({
                              businessId: sa.businessId,
                              businessName: sa.business.name,
                              role: sa.role,
                              permissions: {},
                              features: ['pos', 'schedule', 'customers', 'orders', 'content', 'spending'],
                            }));
                            navigate(`/workplace/${sa.businessId}`);
                          }}
                          style={{
                            width: '100%',
                            background: location.pathname.includes(`/workplace/${sa.businessId}`) ? '#fff3e0' : 'transparent',
                            border: 'none',
                            color: '#333',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            fontSize: '14px',
                            textAlign: 'left',
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>{sa.business.name}</span>
                          <span style={{ fontSize: '12px', color: '#666' }}>{sa.role}</span>
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          setShowWorkDropdown(false);
                          navigate('/workplaces');
                        }}
                        style={{
                          width: '100%',
                          background: 'transparent',
                          border: 'none',
                          color: '#ff6b35',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          marginTop: '4px',
                          borderTop: '1px solid #eee',
                        }}
                      >
                        💼 All workplaces →
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Wallet Balance */}
            <button
              onClick={() => navigate('/wallet')}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              <span>🥭</span>
              {user?.wallet.mangoCredits.toFixed(0) || 0}
            </button>

            {/* Profile Menu */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  color: 'white'
                }}
              >
                {user?.avatarUrl ? (
                  <img 
                    src={user.avatarUrl} 
                    alt="" 
                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  '👤'
                )}
              </button>

              {showProfileMenu && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  padding: '15px',
                  minWidth: '250px',
                  marginTop: '10px'
                }}>
                  {/* User Info */}
                  <div style={{ 
                    padding: '10px',
                    borderBottom: '1px solid #eee',
                    marginBottom: '10px'
                  }}>
                    <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                      {user?.email}
                    </p>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginTop: '8px'
                    }}>
                      <span style={{
                        padding: '3px 10px',
                        background: getTierColor(user?.subscription || 'free') + '20',
                        color: getTierColor(user?.subscription || 'free'),
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {currentPlan.name}
                      </span>
                      <code style={{ fontSize: '11px', color: '#888' }}>
                        {user?.mangoId}
                      </code>
                    </div>
                  </div>

                  {/* Menu Items */}
                  {[
                    { path: '/profile', label: 'My Profile', icon: '👤' },
                    { path: '/settings', label: 'Settings', icon: '⚙️' },
                    { path: '/upgrade', label: 'Upgrade Plan', icon: '⭐' },
                  ].map((item) => (
                    <button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setShowProfileMenu(false);
                      }}
                      style={{
                        width: '100%',
                        background: 'transparent',
                        border: 'none',
                        color: '#333',
                        padding: '12px 10px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '14px',
                        textAlign: 'left'
                      }}
                    >
                      <span>{item.icon}</span>
                      {item.label}
                    </button>
                  ))}

                  <div style={{ borderTop: '1px solid #eee', marginTop: '10px', paddingTop: '10px' }}>
                    <button
                      onClick={() => {
                        logout();
                        navigate('/login');
                      }}
                      style={{
                        width: '100%',
                        background: '#ffebee',
                        border: 'none',
                        color: '#c62828',
                        padding: '12px 10px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '14px'
                      }}
                    >
                      <span>🚪</span>
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        flex: 1,
        maxWidth: '1400px',
        width: '100%',
        margin: '0 auto',
        padding: '25px 20px'
      }}>
        {user && <EventPhotoPrompt />}
        {children}
      </main>

      {/* Footer */}
      <footer style={{
        background: '#1a1a2e',
        color: 'white',
        padding: '30px 20px',
        marginTop: 'auto'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>🥭</span>
            <span style={{ fontSize: '18px', fontWeight: '600' }}>Mango Personal</span>
          </div>
          
          <div style={{ display: 'flex', gap: '20px', fontSize: '14px', color: '#aaa' }}>
            <Link to="/settings" style={{ color: '#aaa', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/settings" style={{ color: '#aaa', textDecoration: 'none' }}>Terms</Link>
            <Link to="/settings" style={{ color: '#aaa', textDecoration: 'none' }}>Help</Link>
            <Link to="/settings" style={{ color: '#aaa', textDecoration: 'none' }}>Contact</Link>
          </div>

          <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
            © 2026 Mango. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Mobile Navigation Overlay */}
      <style>{`
        @media (max-width: 900px) {
          .desktop-nav {
            display: none !important;
          }
          .nav-label {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default Layout;
